import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import {
  HeartHandshake,
  CalendarCheck,
  Home,
  PhoneCall,
  Receipt,
  Siren,
  ArrowRight,
} from "lucide-react";
import { useContent } from "@/context/ContentContext";
import { resolveImage, renderLinkedText } from "@/lib/content";

const reveal = {
  initial: { opacity: 0, y: 40 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: "-80px" },
  transition: { duration: 0.9, ease: [0.22, 1, 0.36, 1] },
};

const MINI_ICONS = [Siren, Receipt, PhoneCall];

export const Services = () => {
  const { content } = useContent();
  const s = content.services;
  return (
  <section
    id="services"
    className="bg-[#1C1917] py-28 lg:py-40"
    data-testid="services-section"
  >
    <div className="mx-auto max-w-7xl px-6 lg:px-10">
      <motion.div {...reveal} className="mb-20 max-w-2xl">
        <p className="mb-6 text-[11px] font-bold uppercase tracking-[0.3em] text-[#C5A059]">
          {s.kicker}
        </p>
        <h2 className="font-display text-4xl font-medium leading-[1.1] tracking-tight text-[#FAF8F5] sm:text-5xl">
          {s.title_line1} <span className="italic text-[#C5A059]">{s.title_line2}</span>
        </h2>
        <p className="mt-6 text-base leading-relaxed text-[#FAF8F5]/70">
          {s.intro}
        </p>
      </motion.div>

      <div className="grid gap-6 lg:grid-cols-12">
        <motion.article
          {...reveal}
          className="group relative overflow-hidden rounded-3xl lg:col-span-7"
          data-testid="service-card-parent-care"
        >
          <img
            src={resolveImage(s.care.image)}
            alt={s.care.image_alt || "NRI parent care in Mohali — a caregiver gently holding an elderly person's hands"}
            className="hero-image-grade h-[420px] w-full object-cover transition-transform duration-700 group-hover:scale-105 lg:h-[520px]"
          />
          <div className="absolute inset-0 bg-[#1C1917]/55 [mask-image:linear-gradient(to_top,black_30%,transparent)]" />
          <div className="absolute bottom-0 p-8 lg:p-12">
            <div className="mb-4 inline-flex h-12 w-12 items-center justify-center rounded-full bg-[#7A2B21] text-[#FAF8F5]">
              <HeartHandshake size={22} />
            </div>
            <h3 className="font-display text-3xl font-medium tracking-tight text-[#FAF8F5]">
              {renderLinkedText(s.care.title)}
            </h3>
            <p className="mt-3 max-w-md text-sm leading-relaxed text-[#FAF8F5]/85">
              {renderLinkedText(s.care.body)}
            </p>
            <Link
              to={s.care.cta_href || "/nri-parent-care"}
              data-testid="service-care-cta"
              className="mt-5 inline-flex items-center gap-2 text-xs font-bold uppercase tracking-wider text-[#C5A059] transition-colors hover:text-[#FAF8F5]"
            >
              {s.care.cta_label || "Explore NRI Parent Care"} <ArrowRight size={14} />
            </Link>
          </div>
        </motion.article>

        <motion.article
          {...reveal}
          transition={{ ...reveal.transition, delay: 0.15 }}
          className="flex flex-col justify-between rounded-3xl bg-[#2C4C3B] p-8 lg:col-span-5 lg:p-12"
          data-testid="service-card-appointments"
        >
          <div>
            <div className="mb-4 inline-flex h-12 w-12 items-center justify-center rounded-full bg-[#C5A059] text-[#1C1917]">
              <CalendarCheck size={22} />
            </div>
            <h3 className="font-display text-3xl font-medium tracking-tight text-[#FAF8F5]">
              {renderLinkedText(s.appointments.title)}
            </h3>
            <p className="mt-3 text-sm leading-relaxed text-[#FAF8F5]/85">
              {renderLinkedText(s.appointments.body)}
            </p>
          </div>
          <p className="mt-8 rounded-2xl border border-[#FAF8F5]/20 bg-[#1C1917]/20 p-5 text-xs leading-relaxed text-[#FAF8F5]/80">
            <span className="font-bold uppercase tracking-wider text-[#C5A059]">
              Honest note:
            </span>{" "}
            {renderLinkedText(s.appointments.honest_note)}
          </p>
        </motion.article>

        <motion.article
          {...reveal}
          className="group relative overflow-hidden rounded-3xl lg:col-span-5"
          data-testid="service-card-property"
        >
          <img
            src={resolveImage(s.property.image)}
            alt={s.property.image_alt || "NRI property management in Chandigarh — a cared-for family home"}
            className="hero-image-grade h-[380px] w-full object-cover transition-transform duration-700 group-hover:scale-105 lg:h-[460px]"
          />
          <div className="absolute inset-0 bg-[#1C1917]/55 [mask-image:linear-gradient(to_top,black_30%,transparent)]" />
          <div className="absolute bottom-0 p-8 lg:p-12">
            <div className="mb-4 inline-flex h-12 w-12 items-center justify-center rounded-full bg-[#7A2B21] text-[#FAF8F5]">
              <Home size={22} />
            </div>
            <h3 className="font-display text-3xl font-medium tracking-tight text-[#FAF8F5]">
              {renderLinkedText(s.property.title)}
            </h3>
            <p className="mt-3 max-w-md text-sm leading-relaxed text-[#FAF8F5]/85">
              {renderLinkedText(s.property.body)}
            </p>
            <Link
              to={s.property.cta_href || "/nri-property-management"}
              data-testid="service-property-cta"
              className="mt-5 inline-flex items-center gap-2 text-xs font-bold uppercase tracking-wider text-[#C5A059] transition-colors hover:text-[#FAF8F5]"
            >
              {s.property.cta_label || "Explore NRI Property Management"} <ArrowRight size={14} />
            </Link>
          </div>
        </motion.article>

        <motion.div
          {...reveal}
          transition={{ ...reveal.transition, delay: 0.15 }}
          className="grid gap-6 sm:grid-cols-3 lg:col-span-7 lg:grid-cols-1 xl:grid-cols-3"
        >
          {s.mini.map((item, i) => {
            const Icon = MINI_ICONS[i] || Siren;
            return (
            <div
              key={item.title}
              className="rounded-3xl border border-[#FAF8F5]/10 bg-[#FAF8F5]/5 p-7 backdrop-blur-sm transition-colors duration-500 hover:border-[#C5A059]/40"
              data-testid={`service-mini-${item.title.toLowerCase().replace(/\s+/g, "-")}`}
            >
              <Icon size={20} className="mb-4 text-[#C5A059]" />
              <h4 className="font-display text-lg font-medium text-[#FAF8F5]">
                {renderLinkedText(item.title)}
              </h4>
              <p className="mt-2 text-xs leading-relaxed text-[#FAF8F5]/65">
                {renderLinkedText(item.body)}
              </p>
            </div>
            );
          })}
        </motion.div>
      </div>
    </div>
  </section>
  );
};
